<script lang="ts">
	import type { TwoFactorAuthVerify } from '../../../types/LoginTypes';
	import FlowContainer from '../../../basic/flow/FlowContainer.svelte';
	import FlowPage from '../../../basic/flow/FlowPage.svelte';
	import FlowStepIndicator from '../../../basic/flow/FlowStepIndicator.svelte';
	import Modal from '../../../basic/modal/Modal.svelte';
	import ModalHeader from '../../../basic/modal/ModalHeader.svelte';
	import { addNotification } from '../../../notification/Notificationstore';
	import { _ } from 'svelte-i18n';
	import { writable } from 'svelte/store';
	import PageTwoFactorAuthVerify from './PageTwoFactorAuthVerify.svelte';
	import PageTwoFactorBackupCode from './PageTwoFactorBackupCode.svelte';
	import PageTwoFactorSetup from './PageTwoFactorSetup.svelte';

	interface Props {
		targetLoginId: number;
		twoFactoryActive: boolean;
		onModalClose: () => void;
		deactivateFunction: (targetLoginId: number) => Promise<void>;
		verifyFunction: (targetLoginId: number, request: TwoFactorAuthVerify) => Promise<string | null>;
	}

	let { targetLoginId, twoFactoryActive, onModalClose, deactivateFunction, verifyFunction }: Props =
		$props();

	const pageStore = writable<number>(0);
	let backupCode = $state('');

	async function verifyFunctionWrapper(targetLoginId: number, request: TwoFactorAuthVerify) {
		let code = await verifyFunction(targetLoginId, request);
		if (!code) {
			addNotification({
				type: 'error',
				message: $_('login.two_factor_auth.err_two_factor_backup_code')
			});
		} else {
			backupCode = code;
		}
	}

	async function deactivateFunctionWrapper(targetLoginId: number) {
		await deactivateFunction(targetLoginId);
		onModalClose();
	}

	function handleModalCloseRequest() {
		//Prevent the closing of the modal when the user is on the backup code
		if ($pageStore != 2) {
			//Hide the modal again
			onModalClose();
		}
	}
</script>

<Modal onModalClose={handleModalCloseRequest}>
	{#snippet header()}
		<div>
			<ModalHeader text={$_('settings.admin.two_factor_auth.lbl_header')}></ModalHeader>
		</div>
	{/snippet}
	{#snippet content()}
		<div class="mb-2">
			<FlowContainer currentPageStore={pageStore}>
				{#snippet flow()}
					<div class="flex justify-center">
						<FlowStepIndicator
							pageNumber={0}
							description={$_('settings.admin.two_factor_auth.bt_setup')}
							iconName={'gear'}
						/>
						<FlowStepIndicator
							pageNumber={1}
							description={$_('settings.admin.two_factor_auth.bt_verify')}
							iconName={'stamp'}
						/>
						<FlowStepIndicator
							pageNumber={2}
							description={$_('settings.admin.two_factor_auth.lbl_recovery')}
							iconName={'shield-keyhole'}
						/>
					</div>
				{/snippet}
				{#snippet pages()}
					<div>
						<FlowPage>
							<PageTwoFactorSetup
								{twoFactoryActive}
								{targetLoginId}
								onModalClose={handleModalCloseRequest}
								deactivateFunction={deactivateFunctionWrapper}
							/>
						</FlowPage>
						<FlowPage>
							<PageTwoFactorAuthVerify {targetLoginId} verifyFunction={verifyFunctionWrapper} />
						</FlowPage>
						<FlowPage>
							<PageTwoFactorBackupCode {backupCode} {onModalClose} />
						</FlowPage>
					</div>
				{/snippet}
			</FlowContainer>
		</div>
	{/snippet}
</Modal>
